/**
 * The evidence domain's FLAG MODULE (ruling 36 as amended 2026-08-29; converted
 * in flags fan-out batch 3).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/evidence/flags` publishes
 * both. `/contributions` carries nothing for flags — this domain has no such
 * door at all, and does not need one to declare a flag.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: each getter reads `descriptor.key` / `descriptor.defaultValue`
 * from the declaration directly above it, so the pair cannot drift, and
 * `flags.test.ts` asserts each key appears exactly once in this file and that no
 * read takes a string literal — the only assertion that can tell a descriptor
 * read from a hardcoded string that happens to be equal today. Flag keys have no
 * cross-site-identity guard of their own (cache keys do), so this file's source
 * case is one of the two things standing between a key and a second spelling.
 * The other is `scripts/check-flag-getter-doors.test.ts`, which spans the door:
 * this case can only see re-mints INSIDE this file, and a re-mint on the app
 * side of the barrel is exactly what the round-1 review planted. That matters
 * more here than in any previous batch, because this is the first domain whose
 * surface publishes a KEY as well as its getters — see the
 * `MEDICATION_HIGHLIGHTS_FLAG_KEY` note below.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection.
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file out of everything under
 * `packages/evidence/src`; `scripts/check-flag-getter-doors.test.ts` walks this
 * module's graph ACROSS package boundaries and refuses `@heidi/design-system`,
 * this package's own `/ui` zone and the app tree anywhere in it, and separately
 * proves the door imports in a bare Node process with no DOM and no side
 * effects.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getters from `@heidi/evidence/flags`, so its importers and
 * their `jest.mock` literals are untouched.
 *
 * THREE THINGS ABOUT THIS DOMAIN'S CONSUMERS, none of which changes where a
 * declaration lives, and all three worth stating because each looks like a
 * problem:
 *
 *   - Six of the twenty-three getters below are read from
 *     `src/components/chat/**`, which is quarantined and shrink-only in both
 *     directions. This move creates NO new Chat coupling: those files import
 *     `@/features/flags`, the app barrel, exactly as they did before, and the
 *     barrel is not a product domain. What would breach the quarantine is a Chat
 *     file importing `@heidi/evidence/flags` directly, and none does.
 *   - CROSS-ROOT READS STAY WHERE THEY ARE. Six of the twenty are read from
 *     `src/features/settings/**` — the team and organisation Evidence settings,
 *     data-management chat retention, billing. Those are settings SURFACES
 *     reading evidence-owned flags, a legal cross-root read of a name the barrel
 *     re-exports. Ownership follows the flag's namespace and the story it gates,
 *     not the directory its widest reader happens to live in.
 *   - ONE KEY IS NOT IN THIS DOMAIN'S NAMESPACE.
 *     `release-ml-scribe-gate-evidence-by-tier` predates the namespace
 *     convention; all four of its production consumers are evidence surfaces
 *     (the chat stream's query limits, the in-session limits hook, the Evidence
 *     billing row and the settings list label), so it is this domain's by
 *     consumers. That is the same reclassification batch 1 made for the fifth
 *     `@heidi/tasks` getter, in the same direction.
 *
 * THIS DOOR CARRIES TWO HOOK/NON-REACT PAIRS, not one, and the second arrived
 * with the whole-class fan-out: `useIsInSessionSurfaceEnabled` and
 * `getIsInSessionSurfaceEnabled` read one key between them, exactly as the
 * medication-highlights pair does, so they take ONE descriptor. Two adjacent
 * pairs is the arrangement where a crossed descriptor is a plausible edit, which
 * is why `flags.test.ts` asserts each pair's two readers share an argument list
 * rather than trusting the source to look right.
 *
 * WHAT DOES NOT BELONG HERE — TEN app-barrel names this domain owns are NOT
 * below, against the twenty-three getters that are, and each is named rather than
 * silently skipped. EIGHT are PostHog, ONE is shape and ONE is a Chat
 * carve-out, which is the whole of the ten:
 *
 *   - `useIsShareEvidenceChatEnabled`, `useIsShareEvidenceChatIntroEnabled`,
 *     `useIsSourceRecommendationsKillSwitchEnabled`,
 *     `useIsSourceRecommendationBannerEnabled`,
 *     `useIsSourceRecommendationPopoverBannerEnabled`,
 *     `useIsEvidenceSummarySuggestionEnabled`, `useIsEvidencePromptsBarEnabled`
 *     and `useIsEvidenceCentralSearchBarEnabled` all resolve through
 *     `useFeatureFlagEnabled` from `@/features/flags/posthog-compat`, which
 *     reads posthog-js on cloud and only falls back to OpenFeature on the
 *     airgapped build. `@heidi/runtime/feature-flags` carries no PostHog
 *     surface, so rewriting one onto the door would change WHICH PROVIDER
 *     answers — a behaviour change wearing a refactor's clothes, and
 *     `monorepo-wave-1-p3-feature-flags` Z4 is the standing rule against it.
 *     Four of them say so in their own bodies, in a commented-out
 *     `useBooleanFlagValue` call kept beside the live read: "Flagsmith
 *     targeting currently lacks the necessary cohorts for this experiment."
 *     They move when the flags are steered to Flagsmith, not before.
 *     `useIsShareEvidenceChatEnabled` additionally carries two
 *     `useManualExposure` pairings, which is a second reason to leave it alone:
 *     the exposure invariant says a move that touches an experiment-backed read
 *     keeps the pairing via an injected port, and that is a bigger job than a
 *     declaration relocation.
 *   - `getEvidenceFailOpenTimeoutOverrideMs` reads a NUMBER
 *     (`getNumberValue`). The door publishes `readFlag` and `readStringFlag`
 *     and no number reader at all, so this is a real disqualifier rather than an
 *     oversight: reaching for `readFlag` and coercing would change the value's
 *     type at the one call site that clamps it
 *     (`getSourceControlFailOpenTimeoutMs`). It moves when the door publishes a
 *     number read, and its classification is named at its app-barrel site too.
 *   - `useIsLinkedContextProvenanceGateKilled` reads an `op-evidence-` key, so it
 *     is this domain's by namespace, but its ONLY reader is
 *     `src/components/chat/**` — quarantined and shrink-only in both directions.
 *     It is carved out of a Chat-quarantine change rather than moved here, so
 *     that change and this one do not collide on the same getter.
 */
import {
  defineFeatureFlag,
  readFlag,
  useBooleanFlag,
} from '@heidi/runtime/feature-flags';

// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor.
//
// `builds: 'all'` on every descriptor. It records that the surfaces these gate
// — the Evidence chat timeline and its reference cards, the library and its
// upload flow, the standalone calculators, the team and organisation Evidence
// settings, the retention screens and the in-note medication highlights — ship
// in the on-prem bundle and are shown or hidden by entitlement
// (`useEvidenceAccess()`) and by these flags' values, not by a build gate.
//
// TWO THINGS THIS FIELD IS NOT. It is not read at runtime: nothing in
// `@heidi/runtime/feature-flags` consults `builds`, and on-prem every flag
// resolves from the ml-scribe bundle (`OnPremFlagBundleSync`), where a key that
// is absent reads `false` — so on-prem posture is set in ml-scribe's
// `scripts/onprem/` flag bundle, never here. And it is not a claim that the
// backend serves what the surface asks for: the appliance mounts Evidence chat
// (completions and history) but not the `/evidence` aggregate nor several
// `/chat` sub-routes, and those reads are closed by the named consts in
// `src/utils/backendSurfaces.ts` (F8), which is also where the chat surface's
// few `isOnPrem`-derived gates live.
// ---------------------------------------------------------------------------

/**
 * In-note medication detection highlights and the Evidence quick actions that
 * hang off them.
 */
export const medicationHighlightsFlag = defineFeatureFlag({
  key: 'release-evidence-medication-suggestions',
  defaultValue: false,
  description: 'In-note medication highlights and the Evidence quick actions',
  builds: 'all',
});

/**
 * THE KEY, PUBLISHED — and the reason it is derived rather than declared.
 *
 * The app barrel exported this constant before the move and batch 1 set the
 * rule that the barrel's export surface stays byte-identical through a
 * conversion, so the name cannot simply vanish. Re-declaring the literal in the
 * barrel would be a SECOND MINTING SITE, which is the one thing ruling 36
 * exists to refuse. This file's own mint-count case cannot see it: that case
 * reads only this source, and the app's suite asserts a VALUE, which a copy
 * satisfies. What refuses it is the repo-wide guard
 * (`scripts/check-flag-getter-doors.test.ts`, 'a declared key is minted ONCE, on
 * both sides of the door'). So the descriptor above stays the single minting site
 * and this reads `key` off it; the barrel re-exports this name beside its
 * getters.
 *
 * Its only reader today is `src/features/flags/index.test.tsx`, which drives
 * both getters below off it rather than off a literal. That is a thin reason to
 * publish a name and a sufficient one: deleting it would be a public-surface
 * change, which is a different decision from where the declaration lives.
 */
export const MEDICATION_HIGHLIGHTS_FLAG_KEY = medicationHighlightsFlag.key;

/**
 * The organisation-level Evidence settings screen — the org admin's blanket
 * policy over the team-level toggles.
 */
export const orgEvidenceSettingsFlag = defineFeatureFlag({
  key: 'release-evidence-org-settings',
  defaultValue: false,
  description: 'Organisation-level Evidence settings screen',
  builds: 'all',
});

/**
 * The team-level Evidence settings page and client enforcement — source
 * control, collection policy, country/jurisdiction — for Together+ team
 * admins. Same key as the PostHog flag this migrates (EVI-2425).
 */
export const teamEvidenceSettingsFlag = defineFeatureFlag({
  key: 'release-team-evidence-settings',
  defaultValue: false,
  description: 'Team-level Evidence settings page and client enforcement',
  builds: 'all',
});

/**
 * Persisting mid-chat session/patient links through `/chat/completions` and
 * enforcing the clinical share gate on web. OFF (default) is soft-link only —
 * outgoing mid-chat ids are not treated as share-disqualifying until the backend
 * documents them. ON consumes the backend persist (share gate + refetch).
 * Flagsmith-managed rather than PostHog, which is what makes it door-readable
 * while its sibling share-chat flags are not.
 */
export const midChatClinicalLinkPersistFlag = defineFeatureFlag({
  key: 'release-evidence-mid-chat-clinical-link-persist',
  defaultValue: false,
  description: 'Mid-chat clinical link persistence and the web share gate',
  builds: 'all',
});

/**
 * The Evidence chat retention controls in team Evidence settings and on the
 * data-management screen, which let team admins configure automatic deletion of
 * standalone Evidence chats.
 */
export const evidenceChatRetentionFlag = defineFeatureFlag({
  key: 'release-evidence-enable-chat-retention',
  defaultValue: false,
  description: 'Evidence chat retention settings',
  builds: 'all',
});

/** The standalone calculators surface — its nav item, library and tools card. */
export const standaloneCalculatorsFlag = defineFeatureFlag({
  key: 'release-evidence-enable-standalone-calculators',
  defaultValue: false,
  description: 'Standalone calculators surface, nav item and tools card',
  builds: 'all',
});

/**
 * Medication Check and Guideline Check rows in the Scribe V2 Tools card.
 * Static, phase-gated Evidence shortcuts (EVI-2332).
 */
export const scribeCdsToolsFlag = defineFeatureFlag({
  key: 'release-evidence-scribe-cds-tools',
  defaultValue: false,
  description: 'CDS Medication Check and Guideline Check rows in the Scribe Tools card',
  builds: 'all',
});

/**
 * The unauthenticated chat sidebar's signup-card layout — the card
 * below the history scroller with a capped list height and a flex spacer, so
 * spare vertical space sits under the card rather than between the list and it.
 */
export const unauthChatSidebarSignupCardFlag = defineFeatureFlag({
  key: 'release-evidence-unauth-chat-sidebar-signup-card',
  defaultValue: false,
  description: 'Unauthenticated chat sidebar signup-card layout',
  builds: 'all',
});

/**
 * One-click "Move to Team" promotion of a personal collection. A kill
 * switch for the affordance rather than for the capability — with it OFF the
 * role and plan gates still apply and only the UI entry points are hidden, so
 * the feature can be turned off without a deploy if the cascade misbehaves.
 */
export const collectionPromoteToTeamFlag = defineFeatureFlag({
  key: 'release-evidence-collection-promote-to-team',
  defaultValue: false,
  description: 'One-click promote-collection-to-team affordance',
  builds: 'all',
});

/** Inline images in Evidence references and in the projects evidence timeline. */
export const evidenceInlineImagesFlag = defineFeatureFlag({
  key: 'release-evidence-enable-inline-images',
  defaultValue: false,
  description: 'Inline images in Evidence references',
  builds: 'all',
});

/**
 * The source-control backend-persistence kill switch. It gates
 * `useSyncSourceControlSelections`, which syncs the in-memory
 * `sourceControlToggleAtom` with `User.evidence_settings.source_control` over
 * PATCH `/v1/user/`. OFF means no backend reads or writes for source control —
 * the atom stays in-memory (selections reset on reload) and the hydration gate
 * unblocks immediately so existing badges still render.
 */
export const evidenceSourceControlBeSyncFlag = defineFeatureFlag({
  key: 'release-evidence-source-control-be-sync',
  defaultValue: false,
  description: 'Source-control backend sync kill switch',
  builds: 'all',
});

/**
 * Team and organisation Evidence settings lock the last L1 source toggle,
 * auto-seed team libraries on enforce, and the backend rejects zero usable
 * sources.
 */
export const evidenceRequireAtLeastOneSourceFlag = defineFeatureFlag({
  key: 'release-evidence-require-at-least-one-source',
  defaultValue: false,
  description: 'Require at least one usable Evidence source',
  builds: 'all',
});

/**
 * Team library enforcement by per-collection Enforce / Optional modes
 * instead of the binary `library_collection_ids_changeable` lock.
 */
export const evidencePerCollectionLibraryModesFlag = defineFeatureFlag({
  key: 'release-evidence-per-collection-library-modes',
  defaultValue: false,
  description: 'Per-collection Enforce/Optional team library modes',
  builds: 'all',
});

/**
 * VisualDx interactive widget in Evidence chat. Defence in depth alongside
 * the backend flag of the same name: with this false the frontend never renders
 * an `InteractiveWidgetBlock` even if a stale or replayed `interactive_widget`
 * chunk reaches the timeline.
 */
export const visualDxFlag = defineFeatureFlag({
  key: 'release-evidence-visualdx',
  defaultValue: false,
  description: 'VisualDx interactive widget in Evidence chat',
  builds: 'all',
});

/**
 * EVI-2454 VisualDx module cards (drug-reaction DDx, gallery, compare,
 * findings, findings-based differential, patient handout). Only takes effect
 * alongside `visualDxFlag`; the backend flag of the same name stops the tools
 * being offered, and this stops a stale chunk rendering if it is off here.
 */
export const visualDxModulesFlag = defineFeatureFlag({
  key: 'release-evidence-visualdx-modules',
  defaultValue: false,
  description: 'VisualDx module cards (drug DDx, gallery, compare, findings, handout)',
  builds: 'all',
});

/**
 * Authenticated NEJM citation figures. Defence in depth alongside the backend
 * flag of the same name: with this false the frontend never requests or renders
 * licensed JPEGs even if a citation still carries figure metadata.
 */
export const partnerNejmFiguresFlag = defineFeatureFlag({
  key: 'release-partner-nejm-figures',
  defaultValue: false,
  description: 'Authenticated NEJM citation figures on the source card',
  builds: 'all',
});

/**
 * Default-off kill switch for residual capabilities added to the harness-based
 * Evidence child, currently calculators and VisualDx. It is separate from the
 * legacy capability flags so rollout or rollback never changes legacy chat.
 */
export const evidenceSubagentResidualCapabilitiesFlag = defineFeatureFlag({
  key: 'release-evidence-subagent-residual-capabilities',
  defaultValue: false,
  description: 'Residual calculator and VisualDx capabilities in the Evidence child',
  builds: 'all',
});

/**
 * The active risk opt-in shown before a user-library document upload proceeds.
 * ON means clinicians explicitly accept the upload risks — they remain
 * responsible for clinical safety, and Evidence cannot reliably interpret images
 * and diagrams — before any file is uploaded. Acceptance is persisted on the
 * user record.
 */
export const libraryUploadRiskOptinFlag = defineFeatureFlag({
  key: 'release-evidence-enable-library-upload-risk-optin',
  defaultValue: false,
  description: 'Risk opt-in before a user-library document upload',
  builds: 'all',
});

/**
 * The image/OCR quality-check UI on user-library document uploads, so an
 * unreadable upload is not silently trusted.
 */
export const libraryUploadQualityChecksFlag = defineFeatureFlag({
  key: 'release-evidence-enable-library-upload-quality-checks',
  defaultValue: false,
  description: 'OCR quality-check UI on library document uploads',
  builds: 'all',
});

/**
 * EVI-2318: International option on Evidence settings clinical-profile country.
 * OFF (default): the option is hidden. ON: clinicians can persist
 * `evidence_settings.country = "International"` to skip regional source
 * boosting. Medical-device-regulated countries (EU/UK including SE_AZ1) stay
 * off — the settings surface also deny-lists those Kinde regions, fail-closed.
 */
export const internationalEvidenceCountryFlag = defineFeatureFlag({
  key: 'release-evidence-international-country-option',
  defaultValue: false,
  description: 'International Evidence country option for non-med-device regions',
  builds: 'all',
});

/**
 * Operational kill switch for the muted Key Safety Notes styling (neutral
 * chrome, model-emitted icons stripped). ON (default) mutes the block for
 * accounts whose Kinde region is known and outside the medical-device regions
 * (UK/EU/SE_AZ1); those regions, and any unresolved region, always keep the
 * certified amber block. OFF returns every account to the certified block.
 */
export const mutedKeySafetyNotesFlag = defineFeatureFlag({
  key: 'op-evidence-muted-key-safety-notes',
  defaultValue: true,
  description: 'Muted Key Safety Notes styling outside medical-device regions',
  builds: 'all',
});

/**
 * Evidence query limits for legacy tiers — the in-session limit derivation, the
 * chat stream's limit read, and the "Usage & Billing" row that reports it.
 *
 * THE ONE KEY HERE THAT IS NOT IN THIS DOMAIN'S NAMESPACE, kept rather than
 * renamed because a key is a frozen external identity: `release-ml-scribe-` is
 * what Flagsmith targets, so respelling it would be switching flags. Ownership
 * follows the consumers, all four of which are evidence surfaces.
 */
export const evidenceQueryLimitsForLegacyTiersFlag = defineFeatureFlag({
  key: 'release-ml-scribe-gate-evidence-by-tier',
  defaultValue: false,
  description: 'Evidence query limits for legacy billing tiers',
  builds: 'all',
});

// ---------------------------------------------------------------------------
// The getters. Each reads the pair off the descriptor above it.
//
// ONE DISCLOSED BEHAVIOURAL DELTA, common to all twenty and a gain rather than
// a risk: `useBooleanFlag` and `readFlag` honour the injected `forceEnableAll`
// override, which the `useBooleanFlagValue` / `getBooleanValue` calls they
// replace did not. That is the choke point the door exists for (design D8) and
// the knob is dev-only. NONE of the twenty app-side bodies carried a
// hand-rolled `process.env` branch, so this domain has no forceAll collapse to
// disclose — unlike patients in batch 2, where all three did.
// ---------------------------------------------------------------------------

/** In-note medication highlights and the Evidence quick actions. */
export const useIsMedicationHighlightsEnabled = (): boolean =>
  useBooleanFlag(medicationHighlightsFlag.key, medicationHighlightsFlag.defaultValue);

/**
 * The same flag for non-React paths — `scribeRecordModel`'s post-note actions
 * read it outside React.
 *
 * ONE DISCLOSED DELTA BEYOND the forceAll note above, and it is the same one
 * `packages/dictate/src/flags.ts` records for `isCursorAwarenessEnabled`: the
 * app-side body was a raw
 * `OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(…)` and `readFlag`
 * short-circuits to `true` when `forceEnableAll` is injected. The raw client
 * read bypassed that override; the door honours it. Behaviour is otherwise
 * identical — `readFlag` resolves the same client on the same default domain.
 */
export const getIsMedicationHighlightsEnabled = (): boolean =>
  readFlag(medicationHighlightsFlag.key, medicationHighlightsFlag.defaultValue);

/** The organisation-level Evidence settings screen. */
export const useIsOrgEvidenceSettingsEnabled = (): boolean =>
  useBooleanFlag(orgEvidenceSettingsFlag.key, orgEvidenceSettingsFlag.defaultValue);

/** The team-level Evidence settings page and client enforcement. */
export const useIsTeamEvidenceSettingsEnabled = (): boolean =>
  useBooleanFlag(teamEvidenceSettingsFlag.key, teamEvidenceSettingsFlag.defaultValue);

/** Mid-chat clinical link persistence and the web share gate. */
export const useIsMidChatClinicalLinkPersistEnabled = (): boolean =>
  useBooleanFlag(
    midChatClinicalLinkPersistFlag.key,
    midChatClinicalLinkPersistFlag.defaultValue
  );

/** The Evidence chat retention settings. */
export const useIsEvidenceChatRetentionEnabled = (): boolean =>
  useBooleanFlag(evidenceChatRetentionFlag.key, evidenceChatRetentionFlag.defaultValue);

/** The standalone calculators surface. */
export const useIsStandaloneCalculatorsEnabled = (): boolean =>
  useBooleanFlag(standaloneCalculatorsFlag.key, standaloneCalculatorsFlag.defaultValue);

/** CDS Medication Check and Guideline Check rows in the Scribe Tools card. */
export const useIsScribeCdsToolsEnabled = (): boolean =>
  useBooleanFlag(scribeCdsToolsFlag.key, scribeCdsToolsFlag.defaultValue);

/** The unauthenticated chat sidebar's signup-card layout. */
export const useIsUnauthChatSidebarSignupCardLayoutEnabled = (): boolean =>
  useBooleanFlag(
    unauthChatSidebarSignupCardFlag.key,
    unauthChatSidebarSignupCardFlag.defaultValue
  );

/** The promote-collection-to-team affordance. */
export const useIsCollectionPromoteToTeamEnabled = (): boolean =>
  useBooleanFlag(
    collectionPromoteToTeamFlag.key,
    collectionPromoteToTeamFlag.defaultValue
  );

/** Inline images in Evidence references. */
export const useIsEvidenceInlineImagesEnabled = (): boolean =>
  useBooleanFlag(evidenceInlineImagesFlag.key, evidenceInlineImagesFlag.defaultValue);

/** The source-control backend-sync kill switch. */
export const useIsEvidenceSourceControlBeSyncEnabled = (): boolean =>
  useBooleanFlag(
    evidenceSourceControlBeSyncFlag.key,
    evidenceSourceControlBeSyncFlag.defaultValue
  );

/** Require at least one usable Evidence source. */
export const useIsEvidenceRequireAtLeastOneSourceEnabled = (): boolean =>
  useBooleanFlag(
    evidenceRequireAtLeastOneSourceFlag.key,
    evidenceRequireAtLeastOneSourceFlag.defaultValue
  );

/** Per-collection Enforce/Optional team library modes. */
export const useIsEvidencePerCollectionLibraryModesEnabled = (): boolean =>
  useBooleanFlag(
    evidencePerCollectionLibraryModesFlag.key,
    evidencePerCollectionLibraryModesFlag.defaultValue
  );

/** The VisualDx interactive widget in Evidence chat. */
export const useIsVisualDxEnabled = (): boolean =>
  useBooleanFlag(visualDxFlag.key, visualDxFlag.defaultValue);

/** The EVI-2454 VisualDx module cards in Evidence chat. */
export const useIsVisualDxModulesEnabled = (): boolean =>
  useBooleanFlag(visualDxModulesFlag.key, visualDxModulesFlag.defaultValue);

/** Authenticated NEJM citation figures on the source card. */
export const useIsPartnerNejmFiguresEnabled = (): boolean =>
  useBooleanFlag(partnerNejmFiguresFlag.key, partnerNejmFiguresFlag.defaultValue);

/**
 * Residual calculator and VisualDx capabilities in the Evidence child.
 *
 * Frozen at the shipped value after 100% Flagsmith rollout (EVI-2631).
 * The descriptor above stays declared; this getter no longer reads it.
 */
export const useIsEvidenceSubagentResidualCapabilitiesEnabled = (): boolean => true;

/** The risk opt-in before a user-library document upload. */
export const useIsLibraryUploadRiskOptinEnabled = (): boolean =>
  useBooleanFlag(libraryUploadRiskOptinFlag.key, libraryUploadRiskOptinFlag.defaultValue);

/** The OCR quality-check UI on library document uploads. */
export const useIsLibraryUploadQualityChecksEnabled = (): boolean =>
  useBooleanFlag(
    libraryUploadQualityChecksFlag.key,
    libraryUploadQualityChecksFlag.defaultValue
  );

/** Evidence query limits for legacy billing tiers. */
export const useIsEvidenceQueryLimitsForLegacyTiersEnabled = (): boolean =>
  useBooleanFlag(
    evidenceQueryLimitsForLegacyTiersFlag.key,
    evidenceQueryLimitsForLegacyTiersFlag.defaultValue
  );

/**
 * International Evidence country option on clinical-profile settings.
 * Shipped at 100% Flagsmith, including managed users (EVI-2609). The getter
 * returns that live value. The descriptor and key stay declared.
 */
export const useIsInternationalEvidenceCountryEnabled = (): boolean => true;

export const useIsMutedKeySafetyNotesEnabled = (): boolean =>
  useBooleanFlag(mutedKeySafetyNotesFlag.key, mutedKeySafetyNotesFlag.defaultValue);

/**
 * The reusable in-session guidance surface — cards, chat, red flags and trials.
 * Parallel to the ChenMed PIN nudges; it neither replaces nor gates ChenMed.
 */
export const inSessionSurfaceFlag = defineFeatureFlag({
  key: 'release-scribe-in-session-surface',
  defaultValue: false,
  description: 'Reusable in-session guidance surface',
  builds: 'all',
});

/** The in-session guidance surface. */
export const useIsInSessionSurfaceEnabled = (): boolean =>
  useBooleanFlag(inSessionSurfaceFlag.key, inSessionSurfaceFlag.defaultValue);

/**
 * The same flag for non-React paths — the agent `ui.session_surface` handler
 * reads it outside React.
 *
 * ONE DISCLOSED DELTA BEYOND the forceAll note above, and it is the same one
 * `getIsMedicationHighlightsEnabled` records: the app-side body was a raw
 * `OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(…)` and `readFlag`
 * short-circuits to `true` when `forceEnableAll` is injected. The raw client read
 * bypassed that override; the door honours it. Behaviour is otherwise identical —
 * `readFlag` resolves the same client on the same default domain. It fails closed
 * on an unset provider either way.
 */
export const getIsInSessionSurfaceEnabled = (): boolean =>
  readFlag(inSessionSurfaceFlag.key, inSessionSurfaceFlag.defaultValue);
